import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Badge } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { PlusCircle, Play, Pause } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { useChartOfAccountsList as useChartOfAccounts } from '../hooks/useChartOfAccounts';
import toast from 'react-hot-toast';

function useRecurring() {
  return useQuery({ queryKey: ['accounts', 'recurring'], queryFn: async () => { const { data } = await api.get('/api/accounts/recurring'); return data as any[]; } });
}

function useCreateRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api/accounts/recurring', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts', 'recurring'] });
      toast.success('Recurring template created!');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'Failed to create template';
      toast.error(msg);
    },
  });
}

function useRunRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/accounts/recurring/run');
      return data;
    },
    onSuccess: (result: any) => {
      qc.invalidateQueries({ queryKey: ['accounts', 'recurring'] });
      toast.success(`Ran ${result?.posted ?? 0} recurring entries.`);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'Failed to run';
      toast.error(msg);
    },
  });
}

export function RecurringEntries() {
  const { data: entries, isLoading } = useRecurring();
  const { data: accounts } = useChartOfAccounts();
  const createMutation = useCreateRecurring();
  const runMutation = useRunRecurring();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ name: '', frequency: 'monthly', nextRunDate: '', debitAccountId: '', creditAccountId: '', amount: '', narration: '' });

  const setField = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }));
  const accountOptions = (accounts || []).map((a: any) => ({ value: a._id, label: `${a.code} — ${a.name}` }));

  const handleCreate = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    if (!form.nextRunDate) { toast.error('Next run date is required'); return; }
    if (!form.debitAccountId) { toast.error('Debit account is required'); return; }
    if (!form.creditAccountId) { toast.error('Credit account is required'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Amount must be > 0'); return; }
    if (form.debitAccountId === form.creditAccountId) { toast.error('Debit and credit accounts must be different'); return; }
    try {
      await createMutation.mutateAsync({ ...form, amount: parseFloat(form.amount) });
      setIsOpen(false);
      setForm({ name: '', frequency: 'monthly', nextRunDate: '', debitAccountId: '', creditAccountId: '', amount: '', narration: '' });
    } catch (_) {
      // error handled by mutation onError
    }
  };

  if (isLoading) return <div aria-busy="true" className="space-y-2"><Skeleton className="h-12 w-full rounded-card" /><Skeleton className="h-[300px] w-full rounded-card" /></div>;

  return (
    <Card className="shadow-xs">
      <CardHeader title="Recurring Entries" subtitle="Templates that post the same debit and credit on a schedule." action={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => runMutation.mutateAsync()} disabled={runMutation.isPending}><Play className="h-4 w-4 mr-1" /> Run Due</Button>
          <Button size="sm" onClick={() => setIsOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> New Template</Button>
        </div>
      } />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[400px]">
          <table className="w-full text-body-sm text-left">
            <thead className="text-label-caps uppercase text-on-surface-variant bg-muted sticky top-0">
              <tr><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Frequency</th><th className="px-4 py-2.5">Debit → Credit</th><th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5">Next Run</th><th className="px-4 py-2.5 text-right">Runs</th><th className="px-4 py-2.5">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(!entries || entries.length === 0) && <tr><td colSpan={7}><EmptyState title="No recurring templates" description="Set one up for rent, salaries or any entry that repeats on the same dates." action={<Button size="sm" variant="outline" onClick={() => setIsOpen(true)}>New Template</Button>} /></td></tr>}
              {entries?.map((e: any) => (
                <tr key={e._id} className="hover:bg-muted/50">
                  <td className="px-4 py-2.5 font-medium">{e.name}</td>
                  <td className="px-4 py-2.5 capitalize">{e.frequency}</td>
                  <td className="px-4 py-2.5 text-xs font-mono">{e.debitAccountId?.code || '—'} → {e.creditAccountId?.code || '—'}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums font-medium">{e.amount == null ? '—' : formatINR(e.amount)}</td>
                  <td className="px-4 py-2.5 font-mono tabular-nums text-muted-foreground">{e.nextRunDate ? new Date(e.nextRunDate).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums">{e.totalRuns ?? 0}{e.maxRuns ? `/${e.maxRuns}` : ''}</td>
                  <td className="px-4 py-2.5">{e.isActive ? <Badge tone="success" className="gap-1"><Play className="h-3 w-3" /> Active</Badge> : <Badge tone="neutral" className="gap-1"><Pause className="h-3 w-3" /> Paused</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
      <Modal open={isOpen} onClose={() => setIsOpen(false)} title="New Recurring Entry" size="md" footer={
        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button><Button onClick={handleCreate} disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating...' : 'Create'}</Button></div>
      }>
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2"><label className="text-sm font-medium">Name</label><Input name="name" value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Monthly Rent" /></div>
          <div><label className="text-sm font-medium">Frequency</label><Select name="frequency" value={form.frequency} onChange={e => setField('frequency', e.target.value)} options={[{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'yearly', label: 'Yearly' }]} /></div>
          <div><label className="text-sm font-medium">Next Run Date</label><Input name="nextRunDate" type="date" value={form.nextRunDate} onChange={e => setField('nextRunDate', e.target.value)} /></div>
          <div><label className="text-sm font-medium">Debit Account</label><Select name="debitAccountId" value={form.debitAccountId} onChange={e => setField('debitAccountId', e.target.value)} options={[{ value: '', label: '— Select —' }, ...accountOptions]} /></div>
          <div><label className="text-sm font-medium">Credit Account</label><Select name="creditAccountId" value={form.creditAccountId} onChange={e => setField('creditAccountId', e.target.value)} options={[{ value: '', label: '— Select —' }, ...accountOptions]} /></div>
          <div><label className="text-sm font-medium">Amount (₹)</label><Input name="amount" type="number" value={form.amount} onChange={e => setField('amount', e.target.value)} /></div>
          <div><label className="text-sm font-medium">Narration</label><Input name="narration" value={form.narration} onChange={e => setField('narration', e.target.value)} placeholder="Optional" /></div>
        </div>
      </Modal>
    </Card>
  );
}
